import React, { useCallback, useEffect, useState } from "react";
import SignatureCanvas from "./SignatureCanvas";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";

const API = import.meta.env.VITE_BACKEND_URL || "http://localhost:8001";

// ─── Types ─────────────────────────────────────────────────────────────────

interface SignatureRecord {
    id: number;
    document_id: string;
    document_hash: string;
    signer_name: string;
    signer_email: string;
    provider: string;
    status: string;
    signed_at: string;
    public_key_pem: string;
    signature_data: string;
}

interface VerifyResult {
    valid: boolean;
    reason: string;
    hash: string;
}

interface Props {
    documentId: string;
    documentText: string;
    userId?: string;
    userName?: string;
    userEmail?: string;
    onInsertSignature?: (base64: string) => void;
}

// ─── Status badge ───────────────────────────────────────────────────────────

const StatusBadge: React.FC<{ valid: boolean }> = ({ valid }) => (
    <span
        style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.3rem",
            padding: "0.18rem 0.6rem",
            borderRadius: "99px",
            fontSize: "0.7rem",
            fontWeight: 700,
            background: valid ? "#14532d" : "#450a0a",
            color: valid ? "#4ade80" : "#f87171",
        }}
    >
        {valid ? "✓ Válida" : "✗ Inválida"}
    </span>
);

// ─── Main component ─────────────────────────────────────────────────────────

const SignaturePanel: React.FC<Props> = ({
    documentId,
    documentText,
    userId,
    userName = "Firmante",
    userEmail = "",
    onInsertSignature,
}) => {
    const [signatures, setSignatures] = useState<SignatureRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const [signing, setSigning] = useState(false);
    const [verifyResult, setVerifyResult] = useState<VerifyResult | null>(null);
    const [verifyingId, setVerifyingId] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [signatureMode, setSignatureMode] = useState<"digital" | "handwritten" | "saved">("digital");
    const [savedSignatures, setSavedSignatures] = useState<any[]>([]);
    const [loadingSaved, setLoadingSaved] = useState(false);

    // ── Load signatures ────────────────────────────────────────────────────────
    const loadSignatures = useCallback(async () => {
        if (!documentId) return;
        setLoading(true);
        try {
            const r = await fetch(`${API}/api/v1/documents/${documentId}/signatures`);
            if (r.ok) setSignatures(await r.json());
        } catch (e) {
            setError("No se pudieron cargar las firmas.");
        } finally {
            setLoading(false);
        }
    }, [documentId]);

    useEffect(() => { loadSignatures(); }, [loadSignatures]);

    const loadSavedSignatures = useCallback(async () => {
        if (!userId || userId === "anonymous") {
            setSavedSignatures([]);
            return;
        }
        setLoadingSaved(true);
        try {
            const { data, error } = await supabase
                .from("user_saved_signatures")
                .select("*")
                .eq("user_id", userId)
                .order("created_at", { ascending: false });

            if (error) throw error;
            setSavedSignatures(data || []);
        } catch (e: any) {
            console.error("[SignaturePanel] Error loading saved signatures:", e);
            toast.error("Error al cargar repositorio de firmas.");
        } finally {
            setLoadingSaved(false);
        }
    }, [userId]);

    useEffect(() => {
        if (signatureMode === "saved") {
            loadSavedSignatures();
        }
    }, [signatureMode, loadSavedSignatures]);

    const saveToRepository = async (base64: string) => {
        if (!userId || userId === "anonymous") {
            console.warn("[SignaturePanel] Cannot save to repository: User is anonymous");
            toast.error("Debes iniciar sesión para guardar firmas en tu perfil.");
            return;
        }
        try {
            const name = window.prompt("Nombre para esta firma (ej. Firma Principal):", `Firma ${savedSignatures.length + 1}`);
            if (!name) {
                return;
            }

            const { data, error } = await supabase
                .from("user_saved_signatures")
                .insert([{
                    user_id: userId,
                    name,
                    signature_data: base64
                }])
                .select();

            if (error) {
                console.error("[SignaturePanel] Supabase insert error:", error);
                throw error;
            }
            toast.success("Firma guardada en tu perfil.");
            if (signatureMode === "saved") loadSavedSignatures();
        } catch (e: any) {
            console.error("[SignaturePanel] Error saving signature:", e);
            toast.error(`No se pudo guardar la firma: ${e.message || 'Error desconocido'}`);
        }
    };

    const deleteSavedSignature = async (id: number) => {
        if (!window.confirm("¿Estás seguro de que deseas eliminar esta firma guardada?")) return;
        try {
            const { error } = await supabase
                .from("user_saved_signatures")
                .delete()
                .eq("id", id);

            if (error) throw error;
            toast.success("Firma eliminada.");
            loadSavedSignatures();
        } catch (e) {
            toast.error("Error al eliminar la firma.");
        }
    };

    // ── Sign document ──────────────────────────────────────────────────────────
    const handleSign = async () => {
        if (!documentText.trim()) {
            setError("El documento está vacío, no se puede firmar.");
            return;
        }
        setSigning(true);
        setError(null);
        try {
            const r = await fetch(`${API}/api/v1/documents/${documentId}/sign`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    document_text: documentText,
                    signer_user_id: userId || null,
                    signer_name: userName,
                    signer_email: userEmail,
                    provider: "internal",
                }),
            });
            if (!r.ok) {
                const err = await r.json().catch(() => ({}));
                throw new Error(err?.detail || "Error al firmar el documento.");
            }
            await loadSignatures();
        } catch (e: any) {
            setError(e.message);
        } finally {
            setSigning(false);
        }
    };

    // ── Verify signature ───────────────────────────────────────────────────────
    const handleVerify = async (sig: SignatureRecord) => {
        setVerifyingId(sig.id);
        setVerifyResult(null);
        try {
            const r = await fetch(`${API}/api/v1/documents/${documentId}/verify`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    document_text: documentText,
                    signature_b64: sig.signature_data,
                    public_key_pem: sig.public_key_pem,
                }),
            });
            if (r.ok) setVerifyResult(await r.json());
        } catch {
            setVerifyResult({ valid: false, reason: "Error de red", hash: "" });
        } finally {
            setVerifyingId(null);
        }
    };

    // ── Revoke ─────────────────────────────────────────────────────────────────
    const handleRevoke = async (sigId: number) => {
        const reason = window.prompt("Motivo de revocación:");
        if (reason === null) return;
        await fetch(`${API}/api/v1/documents/${documentId}/signatures/${sigId}/revoke`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ reason }),
        });
        await loadSignatures();
    };

    // ── Render ─────────────────────────────────────────────────────────────────
    return (
        <div style={styles.root}>
            {/* Header */}
            <div style={styles.header}>
                <span style={styles.headerTitle}>🔐 Firmas Digitales</span>
                <span style={styles.headerCount}>
                    {signatures.length} firma{signatures.length !== 1 ? "s" : ""}
                </span>
            </div>

            {/* Mode Toggle */}
            <div style={styles.modeToggle}>
                <button
                    onClick={() => setSignatureMode("digital")}
                    style={{ ...styles.toggleBtn, ...(signatureMode === "digital" ? styles.toggleBtnActive : {}) }}
                >
                    Criptográfica
                </button>
                <button
                    onClick={() => setSignatureMode("handwritten")}
                    style={{ ...styles.toggleBtn, ...(signatureMode === "handwritten" ? styles.toggleBtnActive : {}) }}
                >
                    Dibujar
                </button>
                <button
                    onClick={() => setSignatureMode("saved")}
                    style={{ ...styles.toggleBtn, ...(signatureMode === "saved" ? styles.toggleBtnActive : {}) }}
                >
                    Repositorio
                </button>
            </div>

            {signatureMode === "digital" ? (
                <div style={styles.signSection}>
                    <div style={styles.signInfo}>
                        <span style={styles.signLabel}>Firmante</span>
                        <span style={styles.signName}>{userName}</span>
                        {userEmail && <span style={styles.signEmail}>{userEmail}</span>}
                    </div>
                    <button
                        style={{ ...styles.signBtn, opacity: signing ? 0.7 : 1 }}
                        onClick={handleSign}
                        disabled={signing || !documentText.trim()}
                    >
                        {signing ? "Firmando…" : "✍️ Firmar digital"}
                    </button>
                </div>
            ) : signatureMode === "handwritten" ? (
                <div style={styles.modalOverlay}>
                    <div style={styles.modalContent}>
                        <div style={styles.modalHeader}>
                            <h3 style={styles.modalTitle}>Dibujar Firma</h3>
                            <button
                                style={styles.closeBtn}
                                onClick={() => setSignatureMode("digital")}
                                title="Cerrar modal"
                            >
                                ✕
                            </button>
                        </div>
                        <SignatureCanvas
                            onCapture={(base64, shouldSave) => {
                                if (onInsertSignature) onInsertSignature(base64);
                                if (shouldSave) {
                                    saveToRepository(base64);
                                }
                                setSignatureMode("digital");
                            }}
                            onCancel={() => setSignatureMode("digital")}
                        />
                    </div>
                </div>
            ) : (
                <div style={styles.savedSection}>
                    {loadingSaved ? (
                        <div style={styles.empty}>Cargando repositorio…</div>
                    ) : savedSignatures.length === 0 ? (
                        <div style={styles.empty}>
                            <p>No tienes firmas guardadas.</p>
                            <button
                                onClick={() => setSignatureMode("handwritten")}
                                style={styles.btnSmall}
                            >
                                <Plus size={14} /> Dibujar nueva
                            </button>
                        </div>
                    ) : (
                        <div style={styles.savedGrid}>
                            {savedSignatures.map((sig) => (
                                <div key={sig.id} style={styles.savedCard}>
                                    <div style={styles.savedCardHeader}>
                                        <span style={styles.savedName}>{sig.name}</span>
                                        <button
                                            onClick={() => deleteSavedSignature(sig.id)}
                                            style={styles.btnIcon}
                                        >
                                            <Trash2 size={12} />
                                        </button>
                                    </div>
                                    <div
                                        draggable={true}
                                        onDragStart={(e) => {
                                            e.dataTransfer.setData("signature-base64", sig.signature_data);
                                            e.dataTransfer.effectAllowed = "copy";
                                        }}
                                        style={{ ...styles.savedPreview, cursor: 'grab' }}
                                        onClick={() => {
                                            if (onInsertSignature) onInsertSignature(sig.signature_data);
                                            toast.success("Firma insertada");
                                            setSignatureMode("digital");
                                        }}
                                    >
                                        <img src={sig.signature_data} alt={sig.name} style={styles.savedImg} />
                                        <div style={styles.savedOverlay}>Arrastra o haz click para insertar</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {error && <div style={styles.errorBox}>{error}</div>}

            {/* Signatures list */}
            {loading ? (
                <div style={styles.empty}>Cargando firmas…</div>
            ) : signatures.length === 0 ? (
                <div style={styles.empty}>Aún no hay firmas en este documento.</div>
            ) : (
                <div style={styles.list}>
                    {signatures.map((sig) => (
                        <div key={sig.id} style={styles.sigCard}>
                            <div style={styles.sigRow}>
                                <div>
                                    <span style={styles.sigName}>{sig.signer_name}</span>
                                    {sig.signer_email && (
                                        <span style={styles.sigEmail}> &lt;{sig.signer_email}&gt;</span>
                                    )}
                                </div>
                                <StatusBadge valid={sig.status === "valid"} />
                            </div>

                            <div style={styles.sigMeta}>
                                <span>🕒 {new Date(sig.signed_at).toLocaleString("es-CO")}</span>
                                <span style={styles.providerBadge}>{sig.provider}</span>
                            </div>

                            <div style={styles.hashRow}>
                                <span style={styles.hashLabel}>SHA-256</span>
                                <code style={styles.hashCode}>{sig.document_hash.slice(0, 20)}…</code>
                            </div>

                            {/* Verify result for this sig */}
                            {verifyResult && verifyingId === null && (
                                <div style={{ ...styles.verifyBox, borderColor: verifyResult.valid ? "#22c55e" : "#ef4444" }}>
                                    <StatusBadge valid={verifyResult.valid} />
                                    <span style={styles.verifyReason}>{verifyResult.reason}</span>
                                </div>
                            )}

                            <div style={styles.sigActions}>
                                <button
                                    style={styles.btnVerify}
                                    onClick={() => handleVerify(sig)}
                                    disabled={verifyingId === sig.id}
                                >
                                    {verifyingId === sig.id ? "Verificando…" : "🔍 Verificar"}
                                </button>
                                <button
                                    style={styles.btnRevoke}
                                    onClick={() => handleRevoke(sig.id)}
                                >
                                    🚫 Revocar
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// ─── Inline styles ──────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
    root: {
        background: "#1e293b",
        borderRadius: "12px",
        border: "1px solid #334155",
        overflow: "hidden",
        fontFamily: "'Inter', sans-serif",
        color: "#e2e8f0",
    },
    header: {
        padding: "0.75rem 1rem",
        borderBottom: "1px solid #334155",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        background: "#0f172a",
    },
    headerTitle: { fontSize: "0.88rem", fontWeight: 700, color: "#f1f5f9" },
    headerCount: { fontSize: "0.72rem", color: "#64748b" },
    signSection: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0.75rem 1rem",
        borderBottom: "1px solid #334155",
        gap: "0.75rem",
        flexWrap: "wrap",
    },
    signInfo: { display: "flex", flexDirection: "column", gap: "0.1rem" },
    signLabel: { fontSize: "0.65rem", color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" },
    signName: { fontSize: "0.85rem", fontWeight: 700, color: "#e2e8f0" },
    signEmail: { fontSize: "0.72rem", color: "#94a3b8" },
    signBtn: {
        padding: "0.45rem 1rem",
        background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
        color: "#fff",
        border: "none",
        borderRadius: "8px",
        fontWeight: 700,
        fontSize: "0.82rem",
        cursor: "pointer",
        boxShadow: "0 2px 8px rgba(99,102,241,0.4)",
        transition: "opacity 0.15s",
    },
    errorBox: {
        margin: "0.5rem 1rem",
        padding: "0.5rem",
        background: "#450a0a",
        color: "#f87171",
        borderRadius: "8px",
        fontSize: "0.78rem",
    },
    empty: {
        padding: "1.5rem",
        textAlign: "center",
        color: "#475569",
        fontSize: "0.82rem",
    },
    list: {
        padding: "0.5rem",
        display: "flex",
        flexDirection: "column",
        gap: "0.5rem",
        maxHeight: "420px",
        overflowY: "auto",
    },
    sigCard: {
        background: "#0f172a",
        borderRadius: "10px",
        border: "1px solid #334155",
        padding: "0.75rem",
        display: "flex",
        flexDirection: "column",
        gap: "0.35rem",
    },
    sigRow: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem" },
    sigName: { fontSize: "0.85rem", fontWeight: 700 },
    sigEmail: { fontSize: "0.72rem", color: "#94a3b8" },
    sigMeta: { display: "flex", gap: "0.75rem", fontSize: "0.72rem", color: "#64748b", flexWrap: "wrap" },
    providerBadge: {
        background: "#1e293b",
        border: "1px solid #334155",
        borderRadius: "6px",
        padding: "0.05rem 0.4rem",
        fontSize: "0.65rem",
        color: "#a5b4fc",
        fontWeight: 700,
        textTransform: "uppercase",
    },
    hashRow: { display: "flex", alignItems: "center", gap: "0.4rem" },
    hashLabel: { fontSize: "0.65rem", color: "#475569", fontWeight: 700 },
    hashCode: { fontSize: "0.68rem", color: "#94a3b8", background: "#0f172a", padding: "0.1rem 0.3rem", borderRadius: "4px" },
    verifyBox: {
        display: "flex", alignItems: "center", gap: "0.5rem",
        padding: "0.4rem", borderRadius: "6px", border: "1px solid",
        background: "#0f172a",
    },
    verifyReason: { fontSize: "0.72rem", color: "#94a3b8" },
    sigActions: { display: "flex", gap: "0.4rem", marginTop: "0.25rem" },
    btnVerify: {
        padding: "0.25rem 0.6rem",
        background: "transparent",
        border: "1px solid #475569",
        color: "#a5b4fc",
        borderRadius: "6px",
        fontSize: "0.72rem",
        cursor: "pointer",
        fontWeight: 600,
    },
    btnRevoke: {
        padding: "0.25rem 0.6rem",
        background: "transparent",
        border: "1px solid #475569",
        color: "#f87171",
        borderRadius: "6px",
        fontSize: "0.72rem",
        cursor: "pointer",
    },
    modeToggle: {
        display: "flex",
        padding: "0.5rem 1rem",
        gap: "0.5rem",
        borderBottom: "1px solid #334155",
        background: "#0f172a",
    },
    toggleBtn: {
        flex: 1,
        padding: "0.3rem",
        fontSize: "0.7rem",
        background: "transparent",
        border: "1px solid #334155",
        color: "#94a3b8",
        borderRadius: "4px",
        cursor: "pointer",
        transition: "all 0.2s",
    },
    toggleBtnActive: {
        background: "#334155",
        color: "#fff",
        border: "1px solid #6366f1",
    },
    canvasSection: {
        padding: "0.75rem 1rem",
        borderBottom: "1px solid #334155",
    },
    savedSection: {
        padding: "1rem",
        borderBottom: "1px solid #334155",
        background: "#0f172a",
    },
    savedGrid: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "0.75rem",
    },
    savedCard: {
        background: "#1e293b",
        borderRadius: "8px",
        border: "1px solid #334155",
        overflow: "hidden",
    },
    savedCardHeader: {
        padding: "0.35rem 0.5rem",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        borderBottom: "1px solid #334155",
        background: "#111827",
    },
    savedName: {
        fontSize: "0.65rem",
        fontWeight: 700,
        color: "#94a3b8",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    savedPreview: {
        height: "80px",
        background: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        position: "relative",
    },
    savedImg: {
        maxWidth: "90%",
        maxHeight: "90%",
        objectFit: "contain",
    },
    savedOverlay: {
        position: "absolute",
        inset: 0,
        background: "rgba(99, 102, 241, 0.8)",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "0.65rem",
        fontWeight: 700,
        opacity: 0,
        transition: "opacity 0.2s",
    },
    btnIcon: {
        background: "none",
        border: "none",
        color: "#64748b",
        cursor: "pointer",
        padding: "2px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    btnSmall: {
        marginTop: "0.5rem",
        padding: "0.3rem 0.6rem",
        background: "#334155",
        color: "#fff",
        border: "none",
        borderRadius: "4px",
        fontSize: "0.7rem",
        display: "flex",
        alignItems: "center",
        gap: "0.3rem",
        cursor: "pointer",
    },
    modalOverlay: {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(15, 23, 42, 0.75)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: "1rem",
    },
    modalContent: {
        backgroundColor: "#1e293b",
        borderRadius: "12px",
        border: "1px solid #334155",
        width: "100%",
        maxWidth: "600px",
        padding: "1.5rem",
        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.3)",
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
    },
    modalHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        borderBottom: "1px solid #334155",
        paddingBottom: "0.75rem",
    },
    modalTitle: {
        fontSize: "1.1rem",
        fontWeight: 600,
        color: "#fff",
        margin: 0,
    },
    closeBtn: {
        background: "none",
        border: "none",
        color: "#94a3b8",
        fontSize: "1.25rem",
        cursor: "pointer",
        padding: "4px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "color 0.2s",
    }
};

// Add hover effect via JS or CSS injection
if (typeof document !== 'undefined') {
    const style = document.createElement('style');
    style.innerHTML = `
        div[style*="savedPreview"]:hover div[style*="savedOverlay"] {
            opacity: 1 !important;
        }
    `;
    document.head.appendChild(style);
}

export default SignaturePanel;
